/**
 * @file Dashboard.tsx
 * @description 
 * Main CRM Dashboard layout component.
 * This component serves as the primary structural shell for the CRM interface.
 * 
 * Key Features:
 * - Responsive Layout: Dynamically switches between a desktop sidebar view and a mobile-friendly full-width layout.
 * - Dynamic Sub-routing: Renders different CRM modules (Portfolio, Messages, Scoring, etc.) based on the active state.
 * - State Synchronization: Listens to external prop changes to sync the active view with external navigation menus (e.g., MobileMenu).
 */

import React, { useState, useEffect } from "react";
import { UserProfile, ActiveView } from "../../types/objects";

// --- CRM Internal View Components ---
// These are the individual modules that get injected into the main content area.
import IPortfolio from "./IPortfolio";
import Messages from "./Messages";
import Documents from "./Documents";
import Contracts from "./Contracts";
import CrmContacts from "./CrmContacts";
import ScoringParsePane from "./ScoringParsePane";
import Reports from "./Reports";
import { INTERNAL_TABS, isInternalView, tabLabel } from "./tabs";

/**
 * Interface defining the expected properties for the Dashboard component.
 * @property {UserProfile} user - Object containing authenticated user data (e.g., username, role).
 * @property {ActiveView} [view] - Optional prop controlled by the parent (App.tsx) to force a specific tab rendering.
 * @property {(view: ActiveView) => void} [onNavigate] - Keeps the parent (mobile menu) in sync when a tab changes here.
 */
interface DashboardProps {
    user: UserProfile;
    view?: ActiveView;
    onNavigate?: (view: ActiveView) => void;
}

export default function Dashboard({ user, view, onNavigate }: DashboardProps) {
    // --- COMPONENT STATE ---
    
    // Manages the currently active CRM module. Defaults to the passed 'view' prop, or "portfolio" as a fallback.
    const [activeTab, setActiveTabState] = useState<ActiveView>(view || "portfolio");

    const setActiveTab = (key: ActiveView) => {
        setActiveTabState(key);
        onNavigate?.(key);
    };
    
    // Tracks the current viewport width to apply mobile-specific layout changes.
    // A threshold of 992px is used to distinguish between desktop/tablet and mobile views.
    const [isMobile, setIsMobile] = useState(window.innerWidth < 992);

    // --- LIFECYCLE HOOKS ---

    /**
     * Effect: Window Resize Listener
     * Attaches a resize event listener to the window on mount.
     * Updates the 'isMobile' boolean state whenever the window crosses the 992px threshold.
     * Cleans up the listener on component unmount to prevent memory leaks.
     */
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 992);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    /**
     * Effect: Prop Synchronization
     * Forces an update to the internal 'activeTab' state whenever the parent component 
     * changes the 'view' prop. This is crucial for keeping external mobile menus in sync with the dashboard.
     */
    useEffect(() => {
        if (view && isInternalView(view)) {
            setActiveTabState(view);
        }
    }, [view]);

    // --- CONFIGURATION ---

    // Sidebar tabs come from ./tabs.ts (shared with the navigation bar and the mobile menu).
    const tabs = INTERNAL_TABS;

    // --- RENDER HELPERS ---

    /**
     * Content router function.
     * Acts as an internal switch to render the appropriate child component based on the 'activeTab' state.
     * 
     * @returns {JSX.Element} The corresponding React component for the active tab.
     */
    const renderContent = () => {
        switch (activeTab) {
            case "portfolio": return <IPortfolio user={user} onNavigate={setActiveTab} />;
            case "messages":  return <Messages user={user} />;
            case "documents": return <Documents user={user} />;
            case "contracts": return <Contracts user={user} />;
            case "contacts":  return <CrmContacts user={user} />;
            case "scoring":   return <ScoringParsePane user={user} />;
            case "reports":   return <Reports user={user} />;
            // Fallback strategy: Always default to Portfolio if an unknown key is encountered.
            default:          return <IPortfolio user={user} onNavigate={setActiveTab} />;
        }
    };

    // --- INLINE STYLES (CSS-in-JS) ---
    // Defined dynamically to easily react to the 'isMobile' state toggle.

    const wrapperStyle: React.CSSProperties = {
        display: "flex",
        backgroundColor: "#f1f5f9",
        minHeight: "100vh",
        // The fixed navigation bar is already offset by <main> in App.tsx
        paddingTop: isMobile ? "0px" : "32px",
        justifyContent: "center",
        paddingBottom: isMobile ? "0px" : "40px"
    };

    const mainContainerStyle: React.CSSProperties = {
        display: "flex",
        flexDirection: "row",
        width: isMobile ? "100%" : "95%",
        maxWidth: "1440px",
        backgroundColor: "#fff",
        borderRadius: isMobile ? "0px" : "32px",
        overflow: "hidden",
        boxShadow: isMobile ? "none" : "0 25px 50px -12px rgba(0, 0, 0, 0.1)",
        border: isMobile ? "none" : "1px solid #e2e8f0"
    };

    const sidebarStyle: React.CSSProperties = {
        width: "300px",
        backgroundColor: "#f8fafc",
        padding: "2.5rem 1.5rem",
        borderRight: "1px solid #e2e8f0",
        display: "flex",
        flexDirection: "column",
        gap: "0.6rem"
    };

    const contentStyle: React.CSSProperties = {
        flexGrow: 1,
        padding: isMobile ? "2rem 1.25rem" : "3.5rem 4rem",
        backgroundColor: "#fff",
        minWidth: 0
    };

    /**
     * Dynamic style generator for individual sidebar tabs.
     * Highlights the tab matching the currently active state.
     * 
     * @param isActive - Boolean indicating if the evaluated tab matches 'activeTab'
     */
    const getTabStyle = (isActive: boolean): React.CSSProperties => ({
        cursor: "pointer",
        backgroundColor: isActive ? "#3b82f6" : "transparent",
        color: isActive ? "#fff" : "#64748b",
        padding: "1.1rem 1.5rem",
        borderRadius: "16px",
        transition: "all 0.2s ease-in-out",
        fontWeight: isActive ? 700 : 600,
        display: "flex",
        alignItems: "center",
        gap: "15px",
        fontSize: "1.1rem"
    });

    // --- COMPONENT RENDER ---

    return (
        <div style={wrapperStyle}>
            <div style={mainContainerStyle}>
                
                {/* 
                  * SIDEBAR NAVIGATION
                  * Conditionally rendered: Only visible on Desktop/Tablet screens.
                  * On mobile devices, the parent component handles navigation via a separate MobileMenu.
                  */}
                {!isMobile && (
                    <aside style={sidebarStyle}>
                        {/* Sidebar Header Section */}
                        <div style={{ marginBottom: "2.5rem", paddingLeft: "0.8rem" }}>
                            <h3 style={{ 
                                fontSize: "0.8rem", 
                                fontWeight: 900, 
                                color: "#94a3b8", 
                                letterSpacing: "0.2em", 
                                textTransform: "uppercase" 
                            }}>
                                CRM Administrace
                            </h3>
                        </div>

                        {/* Iterative rendering of navigation tabs */}
                        {tabs.map((tab) => (
                            <button
                                type="button"
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                style={{ ...getTabStyle(activeTab === tab.key), border: "none", textAlign: "left", width: "100%" }}
                                aria-current={activeTab === tab.key ? "page" : undefined}
                            >
                                <i className={`fa ${tab.icon}`} style={{ width: "24px", fontSize: "1.2rem" }} />
                                <span>{tab.label}</span>
                            </button>
                        ))}

                        {/* 
                          * User Profile Widget (Desktop Only)
                          * Anchored to the bottom of the sidebar flex container using margin-top: auto.
                          */}
                        <div style={{ 
                            marginTop: "auto", 
                            padding: "1.5rem", 
                            backgroundColor: "#f1f5f9", 
                            borderRadius: "20px" 
                        }}>
                            <p style={{ fontSize: "0.85rem", color: "#64748b", fontWeight: 700, margin: "0 0 5px 0" }}>
                                PŘIHLÁŠEN:
                            </p>
                            <p style={{ fontSize: "1.05rem", fontWeight: 800, color: "#0f172a", margin: 0 }}>
                                {user.username}
                            </p>
                        </div>
                    </aside>
                )}

                {/* 
                  * MAIN CONTENT AREA
                  * Houses the currently selected CRM module. Takes up remaining flex space.
                  */}
                <section style={contentStyle} className="dashboard-container">
                    
                    {/* 
                      * MOBILE BREADCRUMBS
                      * Provides context for mobile users since the sidebar is hidden.
                      */}
                    {isMobile && (
                        <div style={{ 
                            marginBottom: "2rem", 
                            display: "flex", 
                            alignItems: "center", 
                            gap: "8px",
                            borderBottom: "1px solid #f1f5f9",
                            paddingBottom: "15px"
                        }}>
                            <span style={{ color: "#94a3b8", fontSize: "0.85rem", fontWeight: 800, textTransform: "uppercase" }}>
                                CRM
                            </span>
                            <span style={{ color: "#cbd5e1" }}>/</span>
                            <span style={{ color: "#3b82f6", fontSize: "0.85rem", fontWeight: 900, textTransform: "uppercase" }}>
                                {tabLabel(activeTab)}
                            </span>
                        </div>
                    )}
                    
                    {/* 
                      * DYNAMIC COMPONENT INJECTION
                      * Mounts the specific component corresponding to the active view state.
                      */}
                    {renderContent()}
                </section>

            </div>
        </div>
    );
}
